// Выбор упражнения из справочника: поиск с первой буквы, несколько групп мышц (списком или на фигуре),
// ⓘ у упражнения — техника и схема. Архивные не предлагаются. Для замены — своя подпись, группа мышц
// заменяемого выбрана сразу (её можно сменить), само заменяемое в списке не показывается.
// Фокус при открытии — на окне, не на поиске: на телефоне не выезжает клавиатура.
// «Только свежие мышцы» — основная мышца восстановлена от 90 % (восстановление — из персонажа).

import { useDeferredValue, useState } from 'react'

import { Badge, Button, EmptyState, IconButton, Input, Modal, Spinner } from '@/components/ui'
import { useExercises, useMuscleLabels, type Exercise } from '@/pages/workouts/api'
import { READY_PERCENT, recoveryTone } from '@/components/muscle-map/muscles'
import { useCharacter } from '@/pages/character/api'
import { EQUIPMENT_LABELS } from '@/pages/workouts/logic'

import { ExerciseInfoSheet } from './ExerciseInfo'
import { ExerciseRecognition } from './ExerciseRecognition'
import { MuscleChips, MuscleMapFilter, MuscleMultiSelect } from './MuscleFilter'

export function ExercisePicker({
  open,
  onClose,
  onPick,
  title = 'Добавить упражнение',
  initialMuscle = '',
  excludeId,
}: {
  open: boolean
  onClose: () => void
  onPick: (exercise: Exercise) => void
  title?: string
  /** Группа мышц, выбранная при открытии (ключ из справочника). */
  initialMuscle?: string
  /** Не показывать это упражнение (то, что заменяем). */
  excludeId?: string
}) {
  const [q, setQ] = useState('')
  const [muscles, setMuscles] = useState<string[]>(initialMuscle ? [initialMuscle] : [])
  const [showMap, setShowMap] = useState(false)
  const [info, setInfo] = useState<Exercise | null>(null)
  const [freshOnly, setFreshOnly] = useState(false)
  const [recognizing, setRecognizing] = useState(false)
  const deferred = useDeferredValue(q)
  const exercises = useExercises(deferred.trim())
  const muscleLabel = useMuscleLabels()
  const recovery = useCharacter().data?.recovery
  const percent = (ex: Exercise) => recovery?.[ex.primary_muscle]?.percent ?? 100
  const list = (exercises.data ?? []).filter(
    (ex) =>
      ex.id !== excludeId &&
      (muscles.length === 0 || muscles.includes(ex.primary_muscle)) &&
      (!freshOnly || percent(ex) >= READY_PERCENT),
  )

  return (
    <Modal open={open} onClose={onClose} title={title} className="sm:max-w-lg" focusPanel>
      <Button className="mb-4" onClick={() => setRecognizing(true)}>Распознать тренажёр</Button>
      <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">
        <Input label="Поиск" placeholder="Например, жим" value={q} onChange={(e) => setQ(e.target.value)} />
        <MuscleMultiSelect value={muscles} onChange={setMuscles} />
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <MuscleChips value={muscles} onChange={setMuscles} />
        <Button size="sm" icon="character" aria-expanded={showMap} onClick={() => setShowMap((v) => !v)} className="ml-auto">
          {showMap ? 'Скрыть схему' : 'Выбрать на схеме'}
        </Button>
      </div>
      {recovery ? (
        <label className="mt-2 flex min-h-10 items-center gap-2 text-sm text-fg">
          <input type="checkbox" className="h-5 w-5" checked={freshOnly} onChange={(e) => setFreshOnly(e.target.checked)} />
          Только свежие мышцы
          <span className="text-xs text-muted">— восстановлены от {READY_PERCENT} %</span>
        </label>
      ) : null}
      {showMap ? (
        <div className="mt-3 rounded-xl border border-border p-3">
          <MuscleMapFilter value={muscles} onChange={setMuscles} />
        </div>
      ) : null}
      <div className="mt-3 min-h-40">
        {exercises.isPending ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : list.length > 0 ? (
          <ul className="divide-y divide-border">
            {list.map((ex) => (
              <li key={ex.id} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    onPick(ex)
                    onClose()
                  }}
                  className="flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-surface-2"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-fg">{ex.name}</span>
                    <span className="block text-xs text-muted">
                      {muscleLabel(ex.primary_muscle)} · {EQUIPMENT_LABELS[ex.equipment]}
                      {percent(ex) < READY_PERCENT ? (
                        <span style={{ color: `var(--muscle-${recoveryTone(percent(ex))})` }}>
                          {' '}
                          · восст. {percent(ex)} %
                        </span>
                      ) : null}
                    </span>
                  </span>
                  {ex.is_custom ? <Badge>своё</Badge> : null}
                </button>
                <IconButton icon="info" label={`Техника и мышцы: ${ex.name}`} onClick={() => setInfo(ex)} />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState icon="list" title="Ничего не нашлось" description="Попробуй другое слово или распознай тренажёр по фото и создай своё упражнение." />
        )}
      </div>
      {info ? (
        <ExerciseInfoSheet exerciseId={info.id} name={info.name} exercise={info} open onClose={() => setInfo(null)} />
      ) : null}
      {recognizing ? <ExerciseRecognition initialText={q} excludeId={excludeId} onClose={() => setRecognizing(false)} onPick={(exercise) => { onPick(exercise); onClose() }} /> : null}
    </Modal>
  )
}
